'use client';

import { useEffect, useMemo, useState } from 'react';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import { SortChips } from '@/components/ui/SortChips';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import { getBankSurfaceColor } from '@/lib/bank-colors';
import { computeBreakdown } from '@/lib/transactions/utils';
import { dueLabel } from '@/lib/transactions/dates';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { StackedBar, breakdownSegments } from './StackedBar';
import { FaturaTransactionItem } from './FaturaTransactionItem';
import { faturaEstimateExplainer, faturaEstimateKind } from './faturaEstimate';
import { visibleFaturaTransactions } from './faturaVisibility';
import type { CategoryIndex } from './categoryIndex';
import type { Transaction } from '@/lib/transactions/types';
import type { ScreenFatura } from './types';

type SortKey = 'date' | 'value' | 'description';
type SortDir = 'asc' | 'desc';

/**
 * Styling contract for the drawer content:
 *  - Bottom (mobile, default Vaul direction): capped at 88vh — leaves a peek.
 *  - Right (desktop, ≥ md): drops the cap and fills viewport height.
 *
 * Note the `data-[vaul-drawer-direction=right]:` selectors (NOT `group-data-`).
 * `group/drawer-content` is on the SAME element as DrawerContent, so child-
 * targeting `group-data-` selectors would not apply to itself.
 */
export const FATURA_DRAWER_CONTENT_CLASS =
  'max-h-[88vh] flex flex-col px-0 ' +
  'data-[vaul-drawer-direction=bottom]:sm:max-w-md ' +
  'data-[vaul-drawer-direction=right]:max-h-none ' +
  'data-[vaul-drawer-direction=right]:h-full ' +
  'data-[vaul-drawer-direction=right]:w-[min(640px,100vw)] ' +
  'data-[vaul-drawer-direction=right]:sm:max-w-xl';

function FaturaBreakdownLoading() {
  return (
    <div className="space-y-2 px-4 pb-3" data-vaul-no-drag>
      <div className="h-1.5 animate-pulse rounded-full bg-surface-sunken" />
      <div className="flex flex-wrap gap-2">
        {[0, 1, 2].map((item) => (
          <div key={item} className="h-4 w-20 animate-pulse rounded bg-surface-sunken" />
        ))}
      </div>
    </div>
  );
}

function FaturaRowsLoading() {
  return (
    <>
      {[0, 1, 2, 3].map((item) => (
        <li key={item} className="py-3">
          <div className="flex items-center gap-3">
            <div className="size-10 shrink-0 animate-pulse rounded-xl bg-surface-sunken" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-3 w-2/3 animate-pulse rounded bg-surface-sunken" />
              <div className="h-2.5 w-1/3 animate-pulse rounded bg-surface-sunken" />
            </div>
            <div className="h-3 w-20 animate-pulse rounded bg-surface-sunken" />
          </div>
        </li>
      ))}
    </>
  );
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fatura: ScreenFatura | null;
  transactions: Transaction[];
  categoryIndex: CategoryIndex;
  onMarkAsPaid: (faturaId: string) => void | Promise<void>;
  isMarkingPaid?: boolean;
  totalCount?: number;
  hasMore?: boolean;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  today?: Date;
}

export function FaturaDetailDrawer({
  open,
  onOpenChange,
  fatura,
  transactions,
  categoryIndex,
  onMarkAsPaid,
  isMarkingPaid = false,
  totalCount,
  hasMore = false,
  isLoading = false,
  isLoadingMore = false,
  onLoadMore,
  today: todayOverride,
}: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  // Keep relative due-date labels accurate when an idle drawer remains open across local midnight.
  // This clock is intentionally independent from the endpoint-authoritative transaction list.
  const [today, setToday] = useState<Date>(() => todayOverride ?? new Date());
  useEffect(() => {
    if (todayOverride) {
      setToday(todayOverride);
      return;
    }
    if (!open) return;

    setToday(new Date());
    let timer: ReturnType<typeof setTimeout>;
    const armNextMidnight = () => {
      const now = new Date();
      const nextMidnight = new Date(
        now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1);
      timer = setTimeout(() => {
        setToday(new Date());
        armNextMidnight();
      }, nextMidnight.getTime() - now.getTime());
    };

    armNextMidnight();
    return () => clearTimeout(timer);
  }, [todayOverride, open]);
  const visibleTransactions = useMemo(
    () => visibleFaturaTransactions(transactions),
    [transactions],
  );
  const breakdown = useMemo(
    () => computeBreakdown(visibleTransactions, 5),
    [visibleTransactions],
  );
  const breakdownItems = useMemo(
    () => breakdownSegments(breakdown, categoryIndex),
    [breakdown, categoryIndex],
  );
  const sortedTxs = useMemo(() => {
    const copy = [...visibleTransactions];
    // Base comparator returns "descending" (largest/newest first); flip for asc.
    if (sortKey === 'value') {
      copy.sort((a, b) => b.amount - a.amount);
    } else if (sortKey === 'description') {
      copy.sort((a, b) => b.merchant.localeCompare(a.merchant, 'pt-BR'));
    } else {
      copy.sort((a, b) => b.date.localeCompare(a.date));
    }
    if (sortDir === 'asc') copy.reverse();
    return copy;
  }, [visibleTransactions, sortKey, sortDir]);

  // Privacy-aware: hidden values stay hidden in the header total and the estimate explainer.
  const formatCurrency = useFormatCurrency();

  if (!fatura) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader className="sr-only">
            <DrawerTitle>Fatura</DrawerTitle>
            <DrawerDescription>Detalhes da fatura selecionada.</DrawerDescription>
          </DrawerHeader>
        </DrawerContent>
      </Drawer>
    );
  }

  const dueDate = fatura.dueDate ? new Date(fatura.dueDate) : null;
  const headerBg = getBankSurfaceColor(fatura.bankColor, 'solid');
  const isPaid = fatura.status === 'paid';
  // Platform-estimated invoice (detected bill payment; bank bill not delivered yet).
  const estimateKind = faturaEstimateKind(fatura);
  const estimateExplainer = faturaEstimateExplainer(fatura, formatCurrency);
  const isInitialLoading = isLoading && sortedTxs.length === 0;
  const transactionTotal = totalCount ?? sortedTxs.length;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className={FATURA_DRAWER_CONTENT_CLASS}>
        <DrawerHeader className="px-0 pb-0">
          <DrawerTitle className="sr-only">Fatura {fatura.bank}</DrawerTitle>
          <DrawerDescription className="sr-only">
            Detalhes da fatura, vencimento e lançamentos associados.
          </DrawerDescription>
        </DrawerHeader>

        {/* Branded header — non-drag so a tap on the bank/ver-no-banco link
            doesn't accidentally dismiss the drawer. */}
        <div className="px-4 pt-1 pb-4" data-vaul-no-drag>
          <div
            className="rounded-2xl p-4 text-white"
            style={{ backgroundColor: headerBg }}
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {fatura.connectorImage && (
                    <img
                      src={fatura.connectorImage}
                      alt=""
                      className="size-5 shrink-0 rounded-full bg-white/20 object-cover"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <p className="truncate text-2xs font-medium tracking-wider text-white/70 uppercase">
                    {fatura.bank}
                    {fatura.last4 && <span className="ml-1 font-mono">····{fatura.last4}</span>}
                    {fatura.label && <span className="ml-1.5 text-white/60 normal-case">{fatura.label}</span>}
                  </p>
                </div>
                <p className="mt-1 text-2xs font-bold tracking-wider text-white/60 uppercase">
                  {estimateKind ? 'Total da fatura · estimado' : 'Total da fatura'}
                </p>
              </div>
              {fatura.billUrl && (
                <a
                  href={fatura.billUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-2xs text-white/80 hover:text-white"
                >
                  Ver no banco
                  <ArrowSquareOutIcon className="size-3" weight="bold" />
                </a>
              )}
            </div>
            <p className="mt-2 font-mono text-3xl font-bold tabular-nums">
              {formatCurrency(fatura.total)}
            </p>
            <div className="mt-3 flex gap-4 text-2xs text-white/80">
              {dueDate && (
                <span>{dueLabel(dueDate, { now: today, paid: isPaid })}</span>
              )}
              {estimateKind && <span>Aguardando o banco</span>}
            </div>
          </div>
        </div>
        {estimateExplainer && (
          <p className="px-4 pb-3 text-2xs text-text-secondary" data-vaul-no-drag>
            {estimateExplainer}
          </p>
        )}

        {/* Scoped breakdown + legend so users can read the colored bar */}
        {isInitialLoading ? (
          <FaturaBreakdownLoading />
        ) : breakdownItems.length > 0 && (
          <div className="space-y-2 px-4 pb-3" data-vaul-no-drag>
            <StackedBar breakdown={breakdown} categoryIndex={categoryIndex} height={6} />
            <ul
              role="list"
              className="flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-muted-foreground"
            >
              {breakdownItems.map((s) => (
                <li key={s.id} className="flex min-w-0 items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="inline-block size-2 shrink-0 rounded-sm"
                    style={{ backgroundColor: s.color }}
                  />
                  <span className="truncate text-text-secondary">{s.label}</span>
                  <span className="font-mono text-text-tertiary tabular-nums">
                    {Math.round(s.pct)}%
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Transactions — `data-vaul-no-drag` so swipes here become scrolls,
            not drawer-dismisses. Without this, Vaul intercepts touch-starts on
            mobile and closes the drawer as soon as the user tries to scroll. */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain px-4 pb-4"
          data-vaul-no-drag
        >
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <h3 className="text-2xs font-bold tracking-wider text-muted-foreground uppercase">
              {isInitialLoading
                ? 'Lançamentos'
                : `${estimateKind === 'paid' ? 'Lançamentos estimados' : 'Lançamentos'} · ${sortedTxs.length}`}
            </h3>
            {!isInitialLoading && transactionTotal > sortedTxs.length && (
              <span className="mr-auto text-2xs font-bold tracking-wider text-muted-foreground uppercase">
                de {transactionTotal}
              </span>
            )}
            {!isInitialLoading && (
              <SortChips
                ariaLabel="Ordenar lançamentos"
                options={[
                  { value: 'date', label: 'Data' },
                  { value: 'value', label: 'Valor' },
                  { value: 'description', label: 'Descrição' },
                ]}
                value={sortKey}
                order={sortDir}
                onChange={(next) => {
                  if (next === sortKey) {
                    setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                  } else {
                    setSortKey(next);
                    setSortDir(next === 'description' ? 'asc' : 'desc');
                  }
                }}
              />
            )}
          </div>
          <ul
            className="divide-y divide-border"
            role={isInitialLoading ? 'status' : undefined}
            aria-label={isInitialLoading ? 'Carregando lançamentos da fatura' : undefined}
          >
            {isInitialLoading ? (
              <FaturaRowsLoading />
            ) : sortedTxs.length > 0 ? (
              sortedTxs.map((tx) => (
                <FaturaTransactionItem key={tx.id} tx={tx} categoryIndex={categoryIndex} />
              ))
            ) : (
              <li className="rounded-xl bg-surface-sunken px-4 py-8 text-center text-sm text-text-tertiary">
                Nenhum lançamento nesta fatura.
              </li>
            )}
          </ul>
          {hasMore && !isInitialLoading && (
            <div className="pt-3">
              <button
                type="button"
                onClick={onLoadMore}
                disabled={isLoadingMore}
                className="flex h-10 w-full items-center justify-center rounded-xl bg-surface-sunken text-sm font-medium text-text-primary hover:bg-neutral-200 dark:hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoadingMore ? 'Carregando...' : 'Carregar mais'}
              </button>
            </div>
          )}
        </div>

        {/* Sticky bottom actions — closed (or overdue) invoices only. An open cycle has
            no final total yet, so "paid" is meaningless for it; the backend enforces the
            same rule and this gate just keeps the action from rendering at all. */}
        {!isPaid && !fatura.source.isOpen && (
          <div
            className="border-t border-border bg-surface px-4 pt-3"
            style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
          >
            <button
              type="button"
              onClick={() => { void onMarkAsPaid(fatura.id); }}
              disabled={isMarkingPaid}
              className="h-12 w-full rounded-xl text-sm font-semibold text-white disabled:cursor-wait disabled:opacity-70"
              style={{ backgroundColor: headerBg }}
            >
              {isMarkingPaid ? 'Marcando...' : 'Marcar como paga'}
            </button>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
}
